import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import { kebabCase } from '@makemydeal/dr-common-utils';

import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';

// components
import type { TextInputEventValue } from '@interstate/components/TextInput';
import { TextInput } from '@makemydeal/dr-shared-components/text-input';
import { Typography } from '@interstate/components/Typography';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// consts/enums
import { STOCK_NUMBER, STOCK_NUMBER_MAX_LENGTH, STOCK_NUMBER_MAX_LENGTH_ERROR, ZERO_WIDTH_SPACE } from '../../../constants';

// styling
import { DetailContainer } from '../trade.interstate.style';
import { useSingleTradeInFormProvider } from '../MultiTradeIn/TradeInFormProvider';

interface StockNumberFieldProps {
    hasTradeIn: boolean;
    isManualFormOpen: boolean;
    disabled?: boolean;
}

export const StockNumberField: React.FC<StockNumberFieldProps> = ({ hasTradeIn, isManualFormOpen, disabled }) => {
    const { tradeInIndex } = useSingleTradeInFormProvider();
    const dispatch = useDispatch();

    const persistedStockNumber = useSelector(tradeInsSelectors.getTradeStockNumber(tradeInIndex));

    const [stockNumber, setStockNumber] = useState(persistedStockNumber);

    useEffect(() => {
        if (isManualFormOpen) {
            setStockNumber(persistedStockNumber || '');
        }
    }, [isManualFormOpen, persistedStockNumber]);

    const onStockNumberChange = useCallback((event: InterstateOnChangeEvent<TextInputEventValue>) => {
        const value = event.target.value;
        setStockNumber(value);
    }, []);

    const onStockNumberBlur = useCallback(() => {
        if (stockNumber === persistedStockNumber) {
            return;
        }
        dispatch(tradeInsActionCreators.updateTradeInStockNumber(stockNumber, tradeInIndex));
    }, [dispatch, stockNumber, persistedStockNumber, tradeInIndex]);

    return (
        <>
            {isManualFormOpen ? (
                <TextInput
                    size="sm"
                    label={STOCK_NUMBER}
                    id={`stock-number-${tradeInIndex}`}
                    data-testid={`stock-number-${tradeInIndex}`}
                    name={kebabCase(STOCK_NUMBER)}
                    onChange={onStockNumberChange}
                    onBlur={onStockNumberBlur}
                    value={stockNumber}
                    disabled={disabled}
                    maxLength={STOCK_NUMBER_MAX_LENGTH}
                    customErrorMessages={{ exceedsMaxLength: STOCK_NUMBER_MAX_LENGTH_ERROR }}
                    selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                />
            ) : (
                <>
                    {hasTradeIn && (
                        <DetailContainer>
                            <Typography variant="label-md" color="sem.color.on-surface.muted" sx={{ marginBottom: '8px' }}>
                                {STOCK_NUMBER}
                            </Typography>
                            <Typography variant="body-md" tag="span" data-testid={`trade-stock-number-value-label-${tradeInIndex}`}>
                                {persistedStockNumber || ZERO_WIDTH_SPACE}
                            </Typography>
                        </DetailContainer>
                    )}
                </>
            )}
        </>
    );
};

export default StockNumberField;
